/**
 * AndroidBackButton — handles the Android hardware back button (and the
 * gesture-nav swipe-from-edge) so the user doesn't accidentally exit the app
 * mid-session. When the back stack is empty (user at the home page), shows a
 * confirmation dialog. On confirm, displays an admin-configurable farewell
 * screen with a countdown + content (thankyou message, custom photo, photo
 * carousel, or AdMob interstitial) before exiting the app.
 *
 * Configuration is fetched from /api/app-settings (quit_screen object):
 *   { enabled, countdown_seconds (3-10), mode, photos[], message, submessage }
 */
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_QS = {
  enabled: true,
  countdown_seconds: 5,
  mode: 'thankyou',
  photos: [],
  message: 'Merci pour votre visite',
  submessage: 'Hypnose clé vous remercie de prendre soin de vous.',
};

const exitApp = async () => {
  try {
    const { App } = await import('@capacitor/app');
    await App.exitApp();
  } catch {
    try { window.close(); } catch { /* noop */ }
  }
};

export default function AndroidBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [showQuitScreen, setShowQuitScreen] = useState(false);
  const [qs, setQs] = useState(DEFAULT_QS);
  const [countdown, setCountdown] = useState(0);
  const [countdownDone, setCountdownDone] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const tickRef = useRef(null);

  // Pre-fetch quit-screen settings so the farewell is instant on click
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/app-settings`);
        const next = { ...DEFAULT_QS, ...((r.data || {}).quit_screen || {}) };
        setQs(next);
      } catch { /* keep defaults */ }
    })();
  }, []);

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    const closeTopmostModal = () => {
      const closeSelectors = [
        '[data-testid="ad-settings-close"]',
        '[data-testid="close-ad-settings"]',
        '[data-testid="quit-screen-admin-close"]',
        '[data-testid="session-audio-close"]',
        '[data-testid="aff-info-close"]',
        '[data-testid="downloads-close"]',
        '[data-testid="close-breathing"]',
        '[data-testid="close-body-scan"]',
        '[data-testid="affirmations-close"]',
        '[data-testid="yoga-close"]',
        '[data-testid="stretching-close"]',
      ];
      for (const sel of closeSelectors) {
        const btn = document.querySelector(sel);
        if (btn && btn.offsetParent !== null) {
          btn.click();
          return true;
        }
      }
      return false;
    };

    const ensureSentinel = () => {
      if (window.history.state?.__appSentinel) return;
      try { window.history.pushState({ __appSentinel: true }, ''); } catch { /* noop */ }
    };
    ensureSentinel();
    const onPop = () => {
      if (!mounted) return;
      ensureSentinel();
      if (closeTopmostModal()) return;
      if (location.pathname === '/') setShowQuitConfirm(true);
      else navigate(-1);
    };
    window.addEventListener('popstate', onPop);
    const popUnsub = () => window.removeEventListener('popstate', onPop);

    let capUnsub = () => {};
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const handler = await App.addListener('backButton', ({ canGoBack }) => {
          if (!mounted) return;
          if (closeTopmostModal()) return;
          if (canGoBack && location.pathname !== '/') { navigate(-1); return; }
          setShowQuitConfirm(true);
        });
        capUnsub = () => { try { handler.remove(); } catch { /* noop */ } };
      } catch { /* noop */ }
    })();

    unsub = () => { popUnsub(); capUnsub(); };
    return () => { mounted = false; if (unsub) unsub(); };
  }, [location.pathname, navigate]);

  // Start the countdown when the quit screen mounts
  useEffect(() => {
    if (!showQuitScreen) return;
    setCountdown(qs.countdown_seconds || 5);
    setCountdownDone(false);
    setPhotoIdx(0);
    // Photo carousel rotation (mode='carousel')
    let photoInterval = null;
    if (qs.mode === 'carousel' && (qs.photos || []).length > 1) {
      photoInterval = setInterval(() => {
        setPhotoIdx((i) => (i + 1) % (qs.photos || []).length);
      }, 1500);
    }
    // Fire AdMob interstitial (mode='ad') — best-effort, swallow web errors
    if (qs.mode === 'ad') {
      (async () => {
        try {
          const { showInterstitial } = await import('../services/admob');
          showInterstitial();
        } catch { /* noop on web */ }
      })();
    }
    // Countdown tick — runs in background WITHOUT visible numbers. When it
    // reaches 0, we DO NOT auto-exit; instead we reveal a close (×) button so
    // the user controls the exit explicitly.
    tickRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(tickRef.current);
          if (photoInterval) clearInterval(photoInterval);
          setCountdownDone(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (photoInterval) clearInterval(photoInterval);
    };
  }, [showQuitScreen, qs]);

  const handleQuit = () => {
    setShowQuitConfirm(false);
    if (!qs.enabled) {
      // Skip the farewell screen, exit immediately
      exitApp();
      return;
    }
    setShowQuitScreen(true);
  };

  const handleCancelQuit = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    setShowQuitScreen(false);
  };

  // === Render: quit confirmation dialog ===
  if (showQuitConfirm && !showQuitScreen) {
    return (
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/85"
        style={{ transform: 'translateZ(0)', isolation: 'isolate' }}
        data-testid="quit-confirm-overlay"
        onClick={() => setShowQuitConfirm(false)}
      >
        <div
          className="relative max-w-sm mx-4 rounded-2xl bg-slate-950 border border-amber-500/30 p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <h3
            className="text-xl font-semibold text-amber-300 mb-2 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {'Quitter l\'application ?'}
          </h3>
          <p className="text-white/70 text-sm text-center mb-5">
            Voulez-vous vraiment quitter Hypnose clé ?
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowQuitConfirm(false)}
              className="flex-1 px-4 py-2.5 rounded-full bg-slate-800 border border-white/15 text-white/80 font-medium hover:bg-slate-700 transition"
              data-testid="quit-cancel"
            >
              Rester
            </button>
            <button
              onClick={handleQuit}
              className="flex-1 px-4 py-2.5 rounded-full bg-amber-500 text-slate-900 font-semibold hover:bg-amber-400 transition"
              data-testid="quit-confirm"
            >
              Quitter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === Render: farewell quit screen with countdown ===
  if (showQuitScreen) {
    const photo = (qs.photos || [])[photoIdx];
    const photoSrc = photo ? `data:${photo.mime || 'image/jpeg'};base64,${photo.data_b64}` : null;
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950" data-testid="quit-thankyou">
        {/* Background visual based on mode */}
        {qs.mode === 'photo' && photoSrc && (
          <img src={photoSrc} alt={photo.name || ''} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        )}
        {qs.mode === 'carousel' && photoSrc && (
          <img
            key={photo.id}
            src={photoSrc}
            alt={photo.name || ''}
            className="absolute inset-0 w-full h-full object-cover opacity-60 transition-opacity duration-700"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />

        <div className="relative z-10 text-center px-8 max-w-md animate-fadeIn">
          <div className="mb-6 text-6xl">🙏</div>
          <h2
            className="text-2xl font-semibold text-amber-300 mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {qs.message || DEFAULT_QS.message}
          </h2>
          <p className="text-white/80 text-sm leading-relaxed mb-6">
            {qs.submessage || DEFAULT_QS.submessage}
          </p>
          {/* Hidden countdown indicator (kept for tests + screen-readers).
              The visible UI shows an × close button only once the countdown
              completes (see top-right of the screen). */}
          <span className="sr-only" data-testid="quit-countdown">{countdown}</span>
          <div className="mt-2">
            <button
              onClick={handleCancelQuit}
              className="text-[11px] px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/70 hover:bg-white/20 transition"
              data-testid="quit-cancel-final"
            >
              Annuler
            </button>
          </div>
        </div>

        {/* Close (×) button — appears in the top-right corner only AFTER the
            silent countdown finishes. Clicking it exits the app. */}
        {countdownDone && (
          <button
            onClick={exitApp}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-amber-500 text-slate-900 shadow-2xl flex items-center justify-center hover:bg-amber-400 transition animate-fadeIn"
            data-testid="quit-exit-now"
            aria-label="Quitter l'application"
          >
            <span className="text-2xl font-bold leading-none">×</span>
          </button>
        )}
      </div>
    );
  }

  return null;
}
